import * as React from 'react';
import styles from './InstallButton.module.css';
import clsx from 'clsx';
import { InstallModal } from './InstallModal';
import { useSelector } from 'react-redux';
import { ReduxState } from 'ducks';
import { getPreloadTargets } from '../util';
import { getOriginalUrlFromResizedUrl } from 'lib/util/imageUrl';
import { openDB } from 'idb';

// IndexedDB configuration (matching service worker config)
const DB_NAME = 'app-storage';
const FILE_STORE = 'file-store';
const DB_VERSION = 1;

const getInitialCachedUrls = async (): Promise<string[]> => {
  try {
    const db = await openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(FILE_STORE)) {
          db.createObjectStore(FILE_STORE, { keyPath: 'url' });
        }
      },
    });

    return (await db.getAllKeys(FILE_STORE)) as string[];
  } catch (error) {
    console.warn('IndexedDB access failed:', error);
    return [];
  }
};

function removeCacheBustingParams(url: string) {
  return url.replace(/\?v=\d+$/, '');
}

interface InstallButtonProps {
  style: React.CSSProperties;
  deferredInstallPrompt: Event | null;
  isIOS: boolean;
  isPWAInstalled: boolean;
  isStandalone: boolean;
}

export const InstallButton: React.FC<InstallButtonProps> = ({
  style,
  deferredInstallPrompt,
  isIOS,
  isPWAInstalled,
  isStandalone,
}) => {
  const [showModal, setShowModal] = React.useState(false);
  const [cachedMediaUrls, setCachedMediaUrls] = React.useState<string[]>([]);

  const digitalMap = useSelector((state: ReduxState) => state.universal.digitalMap.map);
  const settings = useSelector((state: ReduxState) => state.server.settings.all);
  const preloadTargets = React.useMemo(() => getPreloadTargets(digitalMap, settings), [
    digitalMap,
    settings,
  ]);

  // Initialize cached URLs directly from IndexedDB
  React.useEffect(() => {
    if (!isStandalone) return;

    getInitialCachedUrls().then((urls) => {
      if (urls.length > 0) {
        setCachedMediaUrls((prev) => [...new Set([...prev, ...urls])]);
      }
    });
  }, [isStandalone]);

  // Handle the media cache event
  React.useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    const handleMediaCache = (event: MessageEvent) => {
      if (event.data.type === 'MEDIA_CACHED' && event.data.url) {
        setCachedMediaUrls((prevUrls) => [...new Set([...prevUrls, event.data.url])]);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMediaCache);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMediaCache);
    };
  }, []);

  const downloadProgress = React.useMemo(() => {
    const allUrls = new Set(
      [
        ...preloadTargets.editorJsImageUrls,
        ...preloadTargets.editorJsVideoUrls,
        ...preloadTargets.editorJsAudioUrls,
        ...preloadTargets.pinThumbnails,
        ...preloadTargets.pinDetailsFirstImages,
        ...preloadTargets.pinDetailsSecondaryImages,
        ...preloadTargets.categoryIcons,
        ...preloadTargets.menuIcons,
        ...preloadTargets.overlayImages,
        ...(preloadTargets.logoImageUrl ? [encodeURI(preloadTargets.logoImageUrl)] : []),
      ].filter((url) => Boolean(url))
    );

    const originalAllUrlsLength = allUrls.size;
    if (originalAllUrlsLength === 0) return 100;

    for (const url of cachedMediaUrls) {
      const originalUrl = getOriginalUrlFromResizedUrl(removeCacheBustingParams(url));
      const relativeUrl = removeCacheBustingParams(new URL(url).pathname);

      if (allUrls.has(originalUrl)) {
        allUrls.delete(originalUrl);
      } else if (allUrls.has(relativeUrl)) {
        allUrls.delete(relativeUrl);
      }
    }

    console.log('Pending URLs:', Array.from(allUrls));

    const cachedCount = originalAllUrlsLength - allUrls.size;
    return (cachedCount / originalAllUrlsLength) * 100;
  }, [preloadTargets, cachedMediaUrls]);

  return (
    <>
      <button
        className={clsx(
          styles['circular-button'],
          isStandalone && downloadProgress < 100 && styles['downloading'],
          downloadProgress === 100 && styles['download-complete']
        )}
        style={{
          ...style,
          ...(isStandalone &&
            downloadProgress < 100 &&
            ({
              '--progress': `${downloadProgress * 3.6}deg`,
            } as React.CSSProperties)),
        }}
        onClick={(e) => {
          e.stopPropagation();
          setShowModal(true);
        }}
      >
        {isStandalone && downloadProgress < 100 && (
          <>
            <div className={styles['progress-background']} />
            <div className={styles['progress-circle']} />
          </>
        )}
        <img
          src={
            downloadProgress === 100
              ? '/static/images/map/complete_checkmark.svg'
              : '/static/images/map/download.svg'
          }
          alt=""
          className={clsx(
            styles['circular-button-icon'],
            downloadProgress === 100 && styles['download-complete']
          )}
        />
      </button>
      {showModal && (
        <InstallModal
          onClose={() => setShowModal(false)}
          deferredPrompt={deferredInstallPrompt}
          isIOS={isIOS}
          isPWAInstalled={isPWAInstalled}
          downloadProgress={downloadProgress}
          isStandalone={isStandalone}
        />
      )}
    </>
  );
};
